<template>
  <v-dialog
    persistent
    scrollable
    :content-class="contentClass"
    :fullscreen="useFullScreenMode"
    :hide-overlay="false"
    :model-value="showDialog"
    :width="useFullScreenMode ? undefined: width"
    :max-width="useFullScreenMode ? undefined: maxWidth"
    @click:outside="closeModal"
    @keydown.esc="closeOnEsc"
  >
    <v-card :class="extraContentClass">
      <KpHeader
        v-if="showTitle"
        :loading="loading"
        :title="title"
        :show-close="showClose || hostWizard"
        :show-image="showImage"
        :show-icon="showIcon"
        :show-help="showHelp"
        @cancel="$emit('cancel')"
        @help="$emit('help')"
      >
        <template #append-title>
          <slot name="append-title" />
        </template>
      </KpHeader>
      <v-divider v-if="showTitle" />
      <v-card-text
        class="overflow-x-hidden overflow-y-auto"
        :class="{'pa-0':removeBodyMargin}"
        :style="{height: height}"
      >
        <slot :props="{'footerTargetName': '#'+footerTargetName}" />
      </v-card-text>
      <v-card-actions
        v-if="displayFooter"
        class="d-block pa-0"
      >
        <slot
          name="footer"
          :confirm="setDialogTrue"
        >
          <StepperFooter class="flex-fill">
            <form-btn
              v-if="showCancelButton"
              class="confirm-dialog__no"
              data-cy="confirm-dialog__button-cancel"
              :loading="loading"
              @click="$emit('cancel')"
            >
              {{ computedCancelButtonLabel }}
            </form-btn>
            <form-btn
              :class="[confirmationTextColor, 'confirm-dialog__yes']"
              :loading="loading"
              data-kp-anchor="common.confirm-dialog.confirm-button"
              data-cy="confirm-dialog__button-confirm"
              @click="setDialogTrue"
            >
              {{ computedConfirmButtonLabel }}
            </form-btn>
          </StepperFooter>
        </slot>
      </v-card-actions>
      <div
        v-else
        :id="footerTargetName"
      />
    </v-card>
  </v-dialog>
</template>
<script setup>
import { getCurrentInstance, ref, computed } from 'vue';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';
import KpHeader from '~/components/common/keyprod/KpHeader.vue';
import StepperFooter from '~/components/common/stepper/StepperFooter.vue';

// ---------------------------------------------------------------------------
// Props & emits
// ---------------------------------------------------------------------------

const props = defineProps({
  /**
   * The label for the cancel button.
   */
  cancelButtonLabel: {
    type: String,
    default: '',
  },
  /** When we click outside the confirm dialog, closes it */
  closeOnClickOutside: {
    type: Boolean,
    default: false,
  },
  /**
  * The label for the confirm button.
  */
  comfirmButtonLabel: {
    type: String,
    default: '',
  },
  /** Text color class for the confirm button (e.g. text-primary, text-error) */
  confirmationTextColor: {
    type: String,
    default: 'text-white',
  },
  /** Displays the footer or not */
  displayFooter: {
    type: Boolean,
    default: true,
  },
  extraContentClass: {
    type: String,
    default: '',
  },
  forceFullScreen: {
    type: Boolean,
    default: false,
  },
  height: {
    type: String,
    default: '100%',
  },
  hostNumpad: {
    type: Boolean,
    default: false,
  },
  /** Does the confirm dialog hosts a wizard? Has an impact on height. */
  hostWizard: {
    type: Boolean,
    default: false,
  },
  /** Tells if the component is loading */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Removes the body margin */
  removeBodyMargin: {
    type: Boolean,
    default: false,
  },
  /** If the dialog is persistent, it will not close with escape btn */
  persistent: {
    type: Boolean,
    default: false,
  },
  /** Shows the close button or not */
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  /** Shows the close button or not */
  showClose: {
    type: Boolean,
    default: false,
  },
  /** Shows the dialog or not */
  showDialog: {
    type: Boolean,
    default: true,
  },
  showIcon: {
    type: Boolean,
    default: false,
  },
  showImage: {
    type: String,
    default: null,
  },
  // Show the help button
  showHelp: {
    type: Boolean,
    default: false,
  },
  showTitle: {
    type: Boolean,
    default: true,
  },
  /** Dialog title */
  title: {
    type: String,
    default: '',
  },
  /** Default width (for desktop) */
  width: {
    type: [Number, String],
    default: 966,
  },
  /** Optional max-width constraint (for desktop). Defaults to undefined (no constraint). */
  maxWidth: {
    type: [Number, String],
    default: undefined,
  },
  /** Prevents fullscreen mode even on mobile breakpoints */
  disableFullscreen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['cancel', 'help', 'dialog']);

// ---------------------------------------------------------------------------
// Reactive state & computed
// ---------------------------------------------------------------------------

const instance = getCurrentInstance();
const uuid = ref(instance.uid);

const { t } = useI18n();

// Vuetify display helper for responsive design
const { mobile } = useDisplay();

const contentClass = computed(() => {
  let classes = 'confirm-dialog';
  if (props.hostWizard) {
    classes += ' has-wizard';
  } else if (props.hostNumpad) {
    classes += ' has-numpad';
  }
  return classes;
});

const footerTargetName = computed(() => `dialog_footer_${uuid.value}`);

const useFullScreenMode = computed(
  () => (mobile.value && !props.disableFullscreen) || props.forceFullScreen,
);

const computedCancelButtonLabel = computed(() => props.cancelButtonLabel || t('common.cancel'));

const computedConfirmButtonLabel = computed(() => props.comfirmButtonLabel || t('common.confirm'));

// ---------------------------------------------------------------------------
// Methods
// ---------------------------------------------------------------------------

function closeOnEsc() {
  if (props.showClose && !props.persistent) {
    emit('cancel');
  }
}

function closeModal() {
  if (props.closeOnClickOutside) emit('cancel');
}

function setDialogTrue() {
  emit('dialog', true);
}

</script>
